import moment from 'moment';
import React from 'react';
import { getMomentCode } from '../../common/components/utils/Translations/momentCodesAliases';
import makeTranslationHelpers from '../../common/components/utils/Translations/helpers/makeTranslationHelpers';
import { DEFAULT_LANGUAGE, LANGUAGE_CHANGE_EVENT, } from '../../common/components/utils/Translations/helpers/constants';
import { TranslationContext } from '../../common/components/utils/Translations';
import useDocumentDirection from '../../common/components/utils/Translations/useDocumentDirection';
const RootTranslationProvider = ({ language, translations, children, }) => {
    const [currentLanguage, setCurrentLanguage] = React.useState(language);
    const [mergedTranslations, setMergedTranslations] = React.useState(translations);
    const loadedCityTranslationsRef = React.useRef({});
    const loadCitiesTranslations = React.useCallback(async (lang) => {
        if (!lang || lang === DEFAULT_LANGUAGE) {
            return;
        }
        if (loadedCityTranslationsRef.current[lang]) {
            return;
        }
        try {
            const response = await fetch(`/translations/cities/${lang}.json`, {
                cache: 'force-cache',
            });
            loadedCityTranslationsRef.current[lang] = true;
            if (!response.ok) {
                return;
            }
            const cityTranslations = (await response.json());
            setMergedTranslations(previous => ({
                ...previous,
                [lang]: {
                    ...((previous === null || previous === void 0 ? void 0 : previous[lang]) || {}),
                    ...cityTranslations,
                },
            }));
        }
        catch {
            loadedCityTranslationsRef.current[lang] = true;
        }
    }, []);
    React.useEffect(() => {
        setCurrentLanguage(language);
    }, [language]);
    React.useEffect(() => {
        setMergedTranslations(translations);
        loadedCityTranslationsRef.current = {};
    }, [translations]);
    React.useEffect(() => {
        loadCitiesTranslations(currentLanguage);
    }, [currentLanguage, loadCitiesTranslations]);
    React.useEffect(() => {
        const handleLanguageChange = (event) => {
            var _a;
            const nextLanguage = (_a = event.detail) === null || _a === void 0 ? void 0 : _a.language;
            if (!nextLanguage) {
                return;
            }
            setCurrentLanguage(nextLanguage);
        };
        window.addEventListener(LANGUAGE_CHANGE_EVENT, handleLanguageChange);
        return () => {
            window.removeEventListener(LANGUAGE_CHANGE_EVENT, handleLanguageChange);
        };
    }, []);
    React.useEffect(() => {
        moment.locale(getMomentCode(currentLanguage));
    }, [currentLanguage]);
    const context = React.useMemo(() => {
        const locales = [mergedTranslations];
        return {
            locales,
            ...makeTranslationHelpers(currentLanguage, locales),
        };
    }, [currentLanguage, mergedTranslations]);
    useDocumentDirection(context.direction);
    return (<TranslationContext.Provider value={context}>
      {children}
    </TranslationContext.Provider>);
};
export default RootTranslationProvider;
